import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  LOCALE_ID,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import {
  Observable,
  startWith,
  filter,
  map
} from 'rxjs';

import { LOCALE_NAMES, environment, SupportedLocale } from '@env';
import { ScrollDirective } from '@core/common/directives';
import { DropdownContent, DropdownDirective, DropdownToggle } from '@core/dropdown';
import { getCookie, setCookie } from '@core/helpers';
import { AuthState } from '@app/modules/users';

import { LocaleFlagPipe } from '../../pipes/locale-flag.pipe';

const COOKIE_KEY = 'language';

@Component({
  selector: 'shell-language-selector',
  templateUrl: './language-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: 'language-selector.component.scss',
  imports: [
    ScrollDirective,
    AsyncPipe,
    LocaleFlagPipe,
    DropdownContent,
    DropdownToggle,
    DropdownDirective
  ]
})
export class LanguageSelectorComponent implements OnInit {
  public readonly currentLocale = inject<SupportedLocale>(LOCALE_ID);

  private readonly router = inject(Router);
  public readonly authState = inject(AuthState);

  public countryCode = this.authState.countryCode()?.toLowerCase();

  public readonly showCisFlag = this.currentLocale === 'uk';

  @Input()
  public isRowDirection = false;

  public readonly currentPath$: Observable<string> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => this.router.url),
    startWith(this.router.url)
  );

  public readonly supportedLocales =
    environment.supportedLocales.filter(locale => !environment.localesToHide.includes(locale));

  public readonly LOCALE_NAMES = LOCALE_NAMES;

  ngOnInit(): void {
    const storedLocale: string|undefined = getCookie(COOKIE_KEY);

    if(storedLocale !== this.currentLocale) {
      this.updateCurrentLocale(this.currentLocale);
    }
  }

  changeLocale(locale: string): void {
    if(locale === this.currentLocale) {
      return;
    }

    this.updateCurrentLocale(locale);
  }

  generateLocaleUrl(locale: string, currentPath: string): string {
    return `/${locale}${currentPath || ''}`;
  }

  private updateCurrentLocale(locale: string): void {
    // Save cookie for nginx locale detection
    setCookie(COOKIE_KEY, locale, 30);
  }
}
